/**
 * AppController - Main application controller
 * Orchestrates the entire web dashboard experience
 */

import { ApiService } from '@/api/ApiService';
import { StorageService } from '@/utils/StorageService';
import { Dashboard } from '@/components/Dashboard';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { ErrorHandler } from '@/utils/ErrorHandler';

export interface AppState {
  isOnline: boolean;
  selectedDistrict: string | null;
  lastUpdated: Date | null;
  isLoading: boolean;
  error: string | null;
}

export class AppController {
  private dashboard: Dashboard;
  private header: Header;
  private footer: Footer;
  private state: AppState;

  constructor(
    private container: HTMLElement,
    private apiService: ApiService,
    private storageService: StorageService
  ) {
    this.state = {
      isOnline: navigator.onLine,
      selectedDistrict: null,
      lastUpdated: null,
      isLoading: false,
      error: null
    };

    // Initialize components
    this.header = new Header();
    this.dashboard = new Dashboard(this.apiService, this.storageService);
    this.footer = new Footer();

    this.setupEventListeners();
    this.render();
  }

  async initialize(): Promise<void> {
    try {
      this.setState({ isLoading: true, error: null });

      // Try to load cached data first for fast initial render
      const cachedData = await this.storageService.getStats();
      if (cachedData) {
        await this.dashboard.updateData(cachedData);
        this.setState({ lastUpdated: new Date(cachedData.last_updated) });
      }

      // Then fetch fresh data
      await this.refreshData();

    } catch (error) {
      console.error('App initialization failed:', error);
      this.setState({ 
        error: 'Failed to load data. Please check your connection.',
        isLoading: false 
      });
      ErrorHandler.handle(error);
    }
  }

  private async refreshData(): Promise<void> {
    try {
      this.setState({ isLoading: true, error: null });

      // Fetch latest data
      const [stats, districts] = await Promise.all([
        this.apiService.getStats(),
        this.apiService.getDistricts()
      ]);

      // Update dashboard with fresh data
      await this.dashboard.updateData({ stats, districts });

      // Cache the data
      await this.storageService.saveStats({ stats, districts });

      this.setState({ 
        lastUpdated: new Date(),
        isLoading: false,
        error: null 
      });

      console.log('Data refreshed successfully');

    } catch (error) {
      console.error('Data refresh failed:', error);
      
      // Try to use cached data if fresh fetch fails
      const cachedData = await this.storageService.getStats();
      if (cachedData) {
        await this.dashboard.updateData(cachedData);
        this.setState({ 
          lastUpdated: new Date(cachedData.last_updated),
          error: 'Using cached data - connection issues detected',
          isLoading: false 
        });
      } else {
        this.setState({ 
          error: 'Unable to load data. Please check your connection.',
          isLoading: false 
        });
      }
      
      ErrorHandler.handle(error);
    }
  }

  private setupEventListeners(): void {
    // Handle app-level events
    document.addEventListener('app:refresh', () => this.refreshData());
    document.addEventListener('app:online', () => this.handleOnline());
    document.addEventListener('app:offline', () => this.handleOffline());

    // Handle district selection
    document.addEventListener('district:select', (event: CustomEvent) => {
      this.handleDistrictSelect(event.detail.districtCode);
    });

    // Handle data refresh requests
    document.addEventListener('data:refresh', () => this.refreshData());

    // Auto-refresh data every 5 minutes when online
    setInterval(() => {
      if (this.state.isOnline && !document.hidden) {
        this.refreshData();
      }
    }, 5 * 60 * 1000); // 5 minutes
  }

  private handleOnline(): void {
    this.setState({ isOnline: true });
    this.header.updateConnectionStatus(true);
    
    // Refresh data when coming back online
    this.refreshData();
    
    console.log('App is now online');
  }

  private handleOffline(): void {
    this.setState({ isOnline: false });
    this.header.updateConnectionStatus(false);
    
    console.log('App is now offline');
  }

  private handleDistrictSelect(districtCode: string): void {
    this.setState({ selectedDistrict: districtCode });
    this.dashboard.selectDistrict(districtCode);
    
    // Update URL without page reload
    const url = new URL(window.location.href);
    url.searchParams.set('district', districtCode);
    window.history.pushState({ district: districtCode }, '', url.toString());
  }

  private setState(updates: Partial<AppState>): void {
    this.state = { ...this.state, ...updates };
    this.updateUI();
  }

  private updateUI(): void {
    // Update header with current state
    this.header.updateStatus({
      isOnline: this.state.isOnline,
      isLoading: this.state.isLoading,
      lastUpdated: this.state.lastUpdated,
      error: this.state.error
    });

    // Update dashboard loading state
    if (this.state.isLoading) {
      this.dashboard.showLoading();
    }

    // Show error message if needed
    if (this.state.error) {
      this.showErrorMessage(this.state.error);
    }
  }

  private showErrorMessage(message: string): void {
    // Remove existing error messages
    const existingAlert = this.container.querySelector('.error-alert');
    if (existingAlert) {
      existingAlert.remove();
    }

    // Create new error alert
    const alert = document.createElement('div');
    alert.className = 'alert alert-warning error-alert';
    alert.innerHTML = `
      <div style="display: flex; align-items: center; justify-content: space-between;">
        <span>⚠️ ${message}</span>
        <button onclick="this.parentElement.parentElement.remove()" style="
          background: none; 
          border: none; 
          font-size: 1.2rem; 
          cursor: pointer;
          padding: 0;
          color: inherit;
        ">×</button>
      </div>
    `;

    // Insert after header
    const mainContent = this.container.querySelector('.main-content');
    if (mainContent) {
      mainContent.insertBefore(alert, mainContent.firstChild);
    }

    // Auto-hide after 10 seconds
    setTimeout(() => {
      if (alert.parentNode) {
        alert.remove();
      }
    }, 10000);
  }

  private render(): void {
    this.container.innerHTML = `
      <a href="#main-content" class="skip-link">Skip to main content</a>
      <div id="header"></div>
      <main id="main-content" class="main-content" role="main">
        <div id="dashboard"></div>
      </main>
      <div id="footer"></div>
    `;

    // Render components
    const headerElement = this.container.querySelector('#header');
    const dashboardElement = this.container.querySelector('#dashboard');
    const footerElement = this.container.querySelector('#footer');

    if (headerElement) {
      this.header.render(headerElement as HTMLElement);
    }

    if (dashboardElement) {
      this.dashboard.render(dashboardElement as HTMLElement);
    }

    if (footerElement) {
      this.footer.render(footerElement as HTMLElement);
    }
  }

  // Public methods for external access
  public getCurrentState(): AppState {
    return { ...this.state };
  }

  public async forceRefresh(): Promise<void> {
    await this.refreshData();
  }

  public selectDistrict(districtCode: string): void {
    this.handleDistrictSelect(districtCode);
  }
}